// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, AlertTriangle, CheckCircle, Package } from "lucide-react";
import { useInventoryItems, useWarehouses, useWarehouseInventory, useUpcomingEvents, useEventAllocations } from "@/hooks/useInventoryData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const EventAllocationsTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEvents();
  const { data: items } = useInventoryItems();
  const { data: warehouses } = useWarehouses();
  const { data: warehouseInv } = useWarehouseInventory();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [sourceWarehouseId, setSourceWarehouseId] = useState<string | null>(null);
  const { data: allocations, isLoading: allocLoading } = useEventAllocations(selectedEventId);

  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  // Local edits: key = `${itemId}-${venueId}`, value = quantity
  const [localEdits, setLocalEdits] = useState<Record<string, number>>({});

  const selectedEvent = events?.find((e: any) => e.id === selectedEventId);

  // Collect all additional venue IDs that need name resolution
  const additionalVenueIds = useMemo(() => {
    if (!selectedEvent?.additional_venue_ids || !Array.isArray(selectedEvent.additional_venue_ids)) return [];
    return selectedEvent.additional_venue_ids.filter((vid: string) => vid !== selectedEvent.venue_id);
  }, [selectedEvent]);

  // Fetch venue names for additional venues
  const { data: additionalVenues } = useQuery({
    queryKey: ["venue-names", additionalVenueIds],
    queryFn: async () => {
      if (additionalVenueIds.length === 0) return [];
      const { data, error } = await supabase
        .from("venues")
        .select("id, name")
        .in("id", additionalVenueIds);
      if (error) throw error;
      return data || [];
    },
    enabled: additionalVenueIds.length > 0,
  });

  // Get venues for selected event with resolved names
  const eventVenues = useMemo(() => {
    if (!selectedEvent) return [];
    const venues: { id: string; name: string }[] = [];
    if (selectedEvent.venue_id) {
      venues.push({ id: selectedEvent.venue_id, name: selectedEvent.venue_name || "Main Venue" });
    }
    if (additionalVenueIds.length > 0) {
      additionalVenueIds.forEach((vid: string) => {
        if (!venues.find(v => v.id === vid)) {
          const resolved = additionalVenues?.find((v: any) => v.id === vid);
          venues.push({ id: vid, name: resolved?.name || "Venue" });
        }
      });
    }
    return venues;
  }, [selectedEvent, additionalVenueIds, additionalVenues]);

  // Build allocation grid
  const activeItems = (items || []).filter((i: any) => i.is_active !== false);

  const getQty = (itemId: string, venueId: string) => {
    const key = `${itemId}-${venueId}`;
    if (localEdits[key] !== undefined) return localEdits[key];
    const alloc = (allocations || []).find((a: any) => a.item_id === itemId && a.venue_id === venueId);
    return alloc?.quantity_needed || 0;
  };

  const getWarehouseStock = (itemId: string) => {
    if (!sourceWarehouseId) return 0;
    const inv = (warehouseInv || []).find((wi: any) => wi.warehouse_id === sourceWarehouseId && wi.item_id === itemId);
    return inv?.quantity || 0;
  };

  const getTotalNeeded = (itemId: string) => {
    return eventVenues.reduce((sum, v) => sum + getQty(itemId, v.id), 0);
  };

  const handleQtyChange = (itemId: string, venueId: string, val: number) => {
    setLocalEdits(prev => ({ ...prev, [`${itemId}-${venueId}`]: val }));
  };

  const handleSave = async () => {
    if (!selectedEventId) return;
    setSaving(true);
    try {
      // Build upsert data for all items x venues
      const upserts: any[] = [];
      for (const item of activeItems) {
        for (const venue of eventVenues) {
          const qty = getQty(item.id, venue.id);
          if (qty > 0) {
            upserts.push({
              event_id: selectedEventId,
              venue_id: venue.id,
              item_id: item.id,
              quantity_needed: qty,
              source_warehouse_id: sourceWarehouseId,
              status: "planned",
            });
          }
        }
      }

      if (upserts.length > 0) {
        // Delete existing allocations for this event then re-insert
        await supabase.from("manage_event_venue_allocations").delete().eq("event_id", selectedEventId);
        const { error } = await supabase.from("manage_event_venue_allocations").insert(upserts);
        if (error) throw error;
      }

      toast.success("Allocations saved");
      setLocalEdits({});
      queryClient.invalidateQueries({ queryKey: ["event-allocations", selectedEventId] });
    } catch (err) {
      toast.error("Failed to save allocations");
    } finally {
      setSaving(false);
    }
  };

  // Summary
  const totalNeeded = activeItems.reduce((sum: number, i: any) => sum + getTotalNeeded(i.id), 0);
  const shortages = activeItems.filter((i: any) => {
    const stock = getWarehouseStock(i.id);
    const needed = getTotalNeeded(i.id);
    return needed > stock && needed > 0;
  }).length;

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Event & Warehouse Selectors */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="min-w-[280px]">
          <Label>Select Event</Label>
          <Select value={selectedEventId || ""} onValueChange={(v) => { setSelectedEventId(v); setLocalEdits({}); }}>
            <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
            <SelectContent>
              {(events || []).map((e: any) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} ({format(parseLocalDate(e.start_date), "MMM d")} - {format(parseLocalDate(e.end_date), "MMM d, yyyy")})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label>Source Warehouse</Label>
          <Select value={sourceWarehouseId || ""} onValueChange={setSourceWarehouseId}>
            <SelectTrigger><SelectValue placeholder="Select warehouse..." /></SelectTrigger>
            <SelectContent>
              {(warehouses || []).map((w: any) => (
                <SelectItem key={w.id} value={w.id}>{w.name} — {w.city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedEventId && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Package className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold">{totalNeeded}</p><p className="text-sm text-muted-foreground">Total Items Needed</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className={`h-8 w-8 ${shortages > 0 ? "text-destructive" : "text-muted-foreground"}`} />
              <div><p className="text-2xl font-bold">{shortages}</p><p className="text-sm text-muted-foreground">Items with Shortages</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div><p className="text-2xl font-bold">{activeItems.length - shortages}</p><p className="text-sm text-muted-foreground">Fully Covered</p></div>
            </CardContent></Card>
          </div>

          {allocLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : eventVenues.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No venues found for this event.</CardContent></Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  Allocation Grid
                  <Button onClick={handleSave} disabled={saving} size="sm">
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save Allocations
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-background z-10">Item</TableHead>
                        <TableHead>Category</TableHead>
                        {eventVenues.map(v => (
                          <TableHead key={v.id} className="text-center min-w-[100px]">{v.name}</TableHead>
                        ))}
                        <TableHead className="text-center">Total Needed</TableHead>
                        <TableHead className="text-center">Warehouse Stock</TableHead>
                        <TableHead className="text-center">Surplus/Shortage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeItems.map((item: any) => {
                        const totalN = getTotalNeeded(item.id);
                        const stock = getWarehouseStock(item.id);
                        const surplus = stock - totalN;
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="sticky left-0 bg-background z-10 font-medium">{item.name}</TableCell>
                            <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                            {eventVenues.map(v => (
                              <TableCell key={v.id} className="text-center">
                                <Input
                                  type="number"
                                  min={0}
                                  className="w-16 mx-auto text-center"
                                  value={getQty(item.id, v.id)}
                                  onChange={(e) => handleQtyChange(item.id, v.id, parseInt(e.target.value) || 0)}
                                />
                              </TableCell>
                            ))}
                            <TableCell className="text-center font-semibold">{totalN}</TableCell>
                            <TableCell className="text-center">{sourceWarehouseId ? stock : "—"}</TableCell>
                            <TableCell className="text-center">
                              {sourceWarehouseId ? (
                                <span className={surplus < 0 ? "text-red-600 font-bold" : "text-green-600 font-semibold"}>
                                  {surplus >= 0 ? `+${surplus}` : surplus}
                                </span>
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedEventId && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Select an event to manage allocations.</CardContent></Card>
      )}
    </div>
  );
};

export default EventAllocationsTab;
